import { type ReactNode, useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import styles from "./RankTable.module.css";

export const ROW_HEIGHT = 32;
export const ROW_GAP = 2;

export interface RankTableProps<T> {
  rows: T[];
  /** CSS grid template. Columns are per-screen. */
  columns: string;
  /**
   * Row height in px. 32 everywhere on desktop (§I2) — the mobile rankings design is 52px with a
   * 40px thumbnail, which is a different table rather than the same one squeezed, so the
   * virtualiser has to be told.
   */
  rowHeight?: number;
  header: ReactNode;
  renderRow: (row: T, index: number) => ReactNode;
  rowKey: (row: T, index: number) => string | number;
  rowProps?: (row: T, index: number) => Record<string, string | undefined>;
  /** Rendered outside the scroller so it does not scroll away. */
  caption?: ReactNode;
  /**
   * Scrolls a row to the top when this changes — the A–Z jump rail's one requirement.
   *
   * A token rather than a bare index so that jumping to the letter you are already on still
   * scrolls: pressing `S` twice should take you to the top of S both times.
   */
  jumpTo?: { index: number; token: number } | null;
}

/**
 * The dense table.
 *
 * Virtualised because 3,418 rows × eight columns is roughly 27,000 DOM nodes, which scrolls
 * visibly badly. Only the rows on screen exist; the rest is height.
 *
 * The grid template is a prop rather than baked in, so a column can be INSERTED later without
 * restructuring — `MOVE` is Phase 5 (docs/DESIGN-CONFLICTS.md §I3) and deliberately reserves no
 * gutter here.
 */
export function RankTable<T>({
  rows,
  columns,
  header,
  renderRow,
  rowKey,
  rowProps,
  caption,
  jumpTo = null,
  rowHeight = ROW_HEIGHT,
}: RankTableProps<T>) {
  const scroller = useRef<HTMLDivElement>(null);

  const virtual = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => rowHeight + ROW_GAP,
    // A few rows either side, so a fast scroll does not show blank space.
    overscan: 12,
  });

  useEffect(() => {
    if (jumpTo === null) return;
    virtual.scrollToIndex(jumpTo.index, { align: "start" });
    // `virtual` is a new object every render; depending on it would scroll on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpTo?.index, jumpTo?.token]);

  return (
    <div className={styles.wrap}>
      {caption}
      <div className={styles.head} style={{ gridTemplateColumns: columns }}>
        {header}
      </div>
      <div className={styles.scroller} ref={scroller} data-testid="rank-scroller">
        <div className={styles.canvas} style={{ height: `${virtual.getTotalSize()}px` }}>
          {virtual.getVirtualItems().map((item) => {
            const row = rows[item.index]!;
            return (
              <div
                key={rowKey(row, item.index)}
                className={styles.row}
                style={{ gridTemplateColumns: columns, height: `${rowHeight}px`, transform: `translateY(${item.start}px)` }}
                data-index={item.index}
                {...rowProps?.(row, item.index)}
              >
                {renderRow(row, item.index)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { styles as rankTableStyles };
